import React, { useState, useEffect } from 'react';
import { ref, onValue, set } from 'firebase/database';
import { auth, db } from './firebase';
import { reauthenticateWithCredential, EmailAuthProvider, sendEmailVerification } from 'firebase/auth';

const BANKS = [
  'GCB Bank', 'Ecobank Ghana', 'Fidelity Bank Ghana', 'Stanbic Bank Ghana',
  'Cal Bank', 'Zenith Bank Ghana', 'Access Bank Ghana', 'Absa Bank Ghana',
  'UBA Ghana', 'First National Bank', 'Agricultural Development Bank',
  'Republic Bank Ghana', 'Consolidated Bank Ghana',
];

const inp = { width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid #E5E7EB', fontSize: 14, backgroundColor: '#F7F8FA', outline: 'none', boxSizing: 'border-box' };

export default function BankAccountTab() {
  const [bankInfo, setBankInfo]       = useState(null);
  const [form, setForm]               = useState({ bankName: '', accountName: '', accountNumber: '', branch: '', swiftCode: '', momoNumber: '', momoName: '' });
  const [method, setMethod]           = useState('bank');
  const [saving, setSaving]           = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError]     = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [editMode, setEditMode]       = useState(false);
  const [pendingData, setPendingData] = useState(null);

  useEffect(() => {
    onValue(ref(db, 'pharmdev_commission_account'), snap => {
      if (snap.exists()) {
        setBankInfo(snap.val());
        setForm(snap.val());
        setMethod(snap.val().method || 'bank');
        setEditMode(false);
      } else { setEditMode(true); }
    });
  }, []);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSaveClick = () => {
    if (method === 'bank' && (!form.bankName || !form.accountName || !form.accountNumber)) {
      alert('Bank name, account name and account number are required'); return;
    }
    if (method === 'momo' && (!form.momoNumber || !form.momoName)) {
      alert('MoMo number and registered name are required'); return;
    }
    setShowAuthModal(true);
    setAuthPassword(''); setAuthError('');
  };

  // Step 1: Verify password
  const handleAuthAndSave = async () => {
    if (!authPassword) { setAuthError('Enter your password'); return; }
    setAuthLoading(true); setAuthError('');
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, authPassword);
      await reauthenticateWithCredential(user, credential);
      // Step 2: Send email verification
      await sendEmailVerification(user);
      setPendingData({ ...form, method, updatedAt: new Date().toISOString(), updatedBy: user.email });
      setShowAuthModal(false);
      setShowEmailModal(true);
    } catch (e) {
      setAuthError('Incorrect password.');
    } finally { setAuthLoading(false); }
  };

  // Step 3: Confirm email verified then save
  const handleEmailVerifiedSave = async () => {
    try {
      await auth.currentUser.reload();
      if (auth.currentUser.emailVerified) {
        setSaving(true);
        await set(ref(db, 'pharmdev_commission_account'), pendingData);
        setEditMode(false);
        setShowEmailModal(false);
        setPendingData(null);
        setSaving(false);
        alert('✅ PharmDev commission account saved securely.');
      } else {
        alert('⚠️ Email not yet verified. Please click the link in your email first, then tap this button again.');
      }
    } catch (e) { alert('Error saving. Please try again.'); setSaving(false); }
  };

  return (
    <div>
      {/* PASSWORD MODAL */}
      {showAuthModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: '#FFF', borderRadius: 16, padding: 32, maxWidth: 380, width: '90%' }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <p style={{ fontSize: 40, margin: '0 0 10px' }}>🔐</p>
              <h3 style={{ fontWeight: 700, fontSize: 18, margin: '0 0 6px' }}>Step 1 — Verify Password</h3>
              <p style={{ fontSize: 13, color: '#888', margin: 0 }}>Enter your superadmin password. An email verification link will then be sent to {auth.currentUser?.email}.</p>
            </div>
            <input style={{ ...inp, marginBottom: 10 }} type="password" placeholder="Your superadmin password" value={authPassword} onChange={e => { setAuthPassword(e.target.value); setAuthError(''); }} onKeyDown={e => e.key === 'Enter' && handleAuthAndSave()} autoFocus />
            {authError && <p style={{ color: '#B91C1C', fontSize: 13, marginBottom: 10 }}>⚠️ {authError}</p>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={{ flex: 1, padding: '11px', border: '1px solid #E5E7EB', borderRadius: 10, cursor: 'pointer', backgroundColor: '#F7F8FA', color: '#555', fontSize: 14 }} onClick={() => setShowAuthModal(false)}>Cancel</button>
              <button style={{ flex: 2, padding: '11px', backgroundColor: authLoading ? '#A8D5C2' : '#1D9E75', color: '#FFF', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 14 }} onClick={handleAuthAndSave} disabled={authLoading}>
                {authLoading ? 'Sending verification...' : 'Verify & Send Email →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EMAIL VERIFICATION MODAL */}
      {showEmailModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: '#FFF', borderRadius: 16, padding: 32, maxWidth: 420, width: '90%' }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <p style={{ fontSize: 40, margin: '0 0 10px' }}>📧</p>
              <h3 style={{ fontWeight: 700, fontSize: 18, margin: '0 0 6px' }}>Step 2 — Check Your Email</h3>
              <p style={{ fontSize: 13, color: '#555', lineHeight: 1.7, margin: 0 }}>
                A verification link has been sent to:<br />
                <strong>{auth.currentUser?.email}</strong><br /><br />
                Open your email, click the verification link, then come back and tap <strong>I've verified</strong>.
              </p>
            </div>
            <div style={{ backgroundColor: '#FEF3C7', borderRadius: 8, padding: 12, marginBottom: 20, border: '1px solid #FCD34D' }}>
              <p style={{ fontSize: 12, color: '#92400E', margin: 0 }}>⚠️ Check your spam folder if you don't see the email within 1 minute.</p>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={{ flex: 1, padding: '11px', border: '1px solid #E5E7EB', borderRadius: 10, cursor: 'pointer', backgroundColor: '#F7F8FA', color: '#555', fontSize: 14 }} onClick={() => { setShowEmailModal(false); setPendingData(null); }}>Cancel</button>
              <button style={{ flex: 2, padding: '11px', backgroundColor: saving ? '#A8D5C2' : '#1D9E75', color: '#FFF', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 14 }} onClick={handleEmailVerifiedSave} disabled={saving}>
                {saving ? 'Saving...' : "✅ I've verified my email"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontWeight: 700, margin: 0, fontSize: 18 }}>🏦 PharmDev Commission Account</h2>
          <p style={{ color: '#888', fontSize: 13, margin: '4px 0 0' }}>Superadmin only — The account that receives all PharmDev commissions</p>
        </div>
        <span style={{ backgroundColor: '#FEE2E2', color: '#B91C1C', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>🔒 Superadmin Only</span>
      </div>

      <div style={{ backgroundColor: '#FEF3C7', borderRadius: 10, padding: 16, marginBottom: 24, border: '1px solid #FCD34D', display: 'flex', gap: 12 }}>
        <span style={{ fontSize: 24 }}>⚠️</span>
        <div>
          <p style={{ fontWeight: 700, color: '#92400E', margin: '0 0 4px', fontSize: 14 }}>Highly Sensitive — Double Verification Required</p>
          <p style={{ color: '#92400E', fontSize: 13, margin: 0, lineHeight: 1.6 }}>
            Every change requires (1) your superadmin password, then (2) an email verification link. Both must be completed before any change is saved.
          </p>
        </div>
      </div>

      {bankInfo && !editMode && (
        <div style={{ backgroundColor: '#FFF', borderRadius: 12, padding: 24, marginBottom: 20, border: '2px solid #1D9E75' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontWeight: 700, margin: 0, fontSize: 15 }}>✅ Current Commission Account</h3>
            <button style={{ padding: '8px 16px', backgroundColor: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }} onClick={() => setEditMode(true)}>✏️ Edit Account</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {bankInfo.method === 'bank' ? (
              <>
                <div style={{ backgroundColor: '#F7F8FA', borderRadius: 8, padding: 12 }}>
                  <p style={{ fontSize: 11, color: '#888', margin: '0 0 4px', textTransform: 'uppercase' }}>Bank</p>
                  <p style={{ fontSize: 16, fontWeight: 700, color: '#1A1A1A', margin: 0 }}>{bankInfo.bankName}</p>
                </div>
                <div style={{ backgroundColor: '#F7F8FA', borderRadius: 8, padding: 12 }}>
                  <p style={{ fontSize: 11, color: '#888', margin: '0 0 4px', textTransform: 'uppercase' }}>Account Name</p>
                  <p style={{ fontSize: 16, fontWeight: 700, color: '#1A1A1A', margin: 0 }}>{bankInfo.accountName}</p>
                </div>
                <div style={{ backgroundColor: '#E8F8F2', borderRadius: 8, padding: 12 }}>
                  <p style={{ fontSize: 11, color: '#888', margin: '0 0 4px', textTransform: 'uppercase' }}>Account Number</p>
                  <p style={{ fontSize: 20, fontWeight: 800, color: '#1D9E75', margin: 0, letterSpacing: 2 }}>{bankInfo.accountNumber}</p>
                </div>
                {bankInfo.branch && (
                  <div style={{ backgroundColor: '#F7F8FA', borderRadius: 8, padding: 12 }}>
                    <p style={{ fontSize: 11, color: '#888', margin: '0 0 4px', textTransform: 'uppercase' }}>Branch</p>
                    <p style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A', margin: 0 }}>{bankInfo.branch}</p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{ backgroundColor: '#E8F8F2', borderRadius: 8, padding: 12 }}>
                  <p style={{ fontSize: 11, color: '#888', margin: '0 0 4px', textTransform: 'uppercase' }}>MTN MoMo Number</p>
                  <p style={{ fontSize: 20, fontWeight: 800, color: '#1D9E75', margin: 0 }}>{bankInfo.momoNumber}</p>
                </div>
                <div style={{ backgroundColor: '#F7F8FA', borderRadius: 8, padding: 12 }}>
                  <p style={{ fontSize: 11, color: '#888', margin: '0 0 4px', textTransform: 'uppercase' }}>Registered Name</p>
                  <p style={{ fontSize: 16, fontWeight: 700, color: '#1A1A1A', margin: 0 }}>{bankInfo.momoName}</p>
                </div>
              </>
            )}
          </div>
          <p style={{ fontSize: 12, color: '#888', margin: '14px 0 0' }}>
            Last updated: {bankInfo.updatedAt ? new Date(bankInfo.updatedAt).toLocaleString() : 'N/A'} by {bankInfo.updatedBy || 'N/A'}
          </p>
        </div>
      )}

      {editMode && (
        <div style={{ backgroundColor: '#FFF', borderRadius: 12, padding: 24, border: '1px solid #E5E7EB' }}>
          <h3 style={{ fontWeight: 700, marginBottom: 16, fontSize: 15 }}>
            {bankInfo ? '✏️ Update Commission Account' : '➕ Set Up Commission Account'}
          </h3>
          <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
            {[{ id: 'bank', label: '🏦 Bank Transfer' }, { id: 'momo', label: '📱 MTN MoMo' }].map(m => (
              <button key={m.id} style={{ flex: 1, padding: '12px', borderRadius: 10, border: `2px solid ${method === m.id ? '#1D9E75' : '#E5E7EB'}`, backgroundColor: method === m.id ? '#E8F8F2' : '#FFF', cursor: 'pointer', fontWeight: method === m.id ? 700 : 400, color: method === m.id ? '#1D9E75' : '#555', fontSize: 14 }} onClick={() => setMethod(m.id)}>
                {m.label}
              </button>
            ))}
          </div>

          {method === 'bank' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {[
                { label: 'Bank Name *', key: 'bankName', isSelect: true },
                { label: 'Account Name *', key: 'accountName', ph: 'Name as on the account' },
                { label: 'Account Number *', key: 'accountNumber', ph: 'Bank account number' },
                { label: 'Branch (optional)', key: 'branch', ph: 'e.g. Kumasi Main Branch' },
                { label: 'SWIFT Code (optional)', key: 'swiftCode', ph: 'For international transfers' },
              ].map(field => (
                <div key={field.key} style={{ marginBottom: 4 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>{field.label}</label>
                  {field.isSelect ? (
                    <select style={inp} value={form.bankName} onChange={e => upd('bankName', e.target.value)}>
                      <option value="">Select bank</option>
                      {BANKS.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  ) : (
                    <input style={inp} placeholder={field.ph} value={form[field.key] || ''} onChange={e => upd(field.key, e.target.value)} />
                  )}
                </div>
              ))}
            </div>
          )}

          {method === 'momo' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>MTN MoMo Number *</label>
                <input style={inp} placeholder="e.g. 0244000000" value={form.momoNumber || ''} onChange={e => upd('momoNumber', e.target.value)} maxLength={10} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Registered Name *</label>
                <input style={inp} placeholder="Name on MoMo account" value={form.momoName || ''} onChange={e => upd('momoName', e.target.value)} />
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            {bankInfo && <button style={{ padding: '12px 20px', border: '1px solid #E5E7EB', borderRadius: 10, cursor: 'pointer', backgroundColor: '#F7F8FA', color: '#555', fontSize: 14 }} onClick={() => setEditMode(false)}>Cancel</button>}
            <button style={{ flex: 1, padding: '13px', backgroundColor: saving ? '#A8D5C2' : '#1D9E75', color: '#FFF', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 14 }} onClick={handleSaveClick} disabled={saving}>
              {saving ? 'Saving...' : '🔐 Save with Password + Email Verification'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}